import React, { useState, useEffect } from "react";
import { Box, Button, Flex, Skeleton, Text, CopyButton, Tooltip } from "@mantine/core";

const ACTION_LABELS = {
  elaborateNotePrompt: "Elaborate",
  rewriteNotePrompt: "Rewrite",
  polishNotePrompt: "Polish",
  behavior: "Behavior",
  response: "Response",
  plan: "Plan",
};

const wordCount = (text) => (text?.trim().match(/\S+/g) || []).length;

const ComparePanel = ({ action, option1, option2, loading1, loading2, option1Model, option2Model, onUse, onCancel, onRegenerate, onArrange }) => {
  const actionLabel = ACTION_LABELS[action] || "AI Suggestions";

  // Randomize which model fills slot 1 vs slot 2 each time a new request starts
  const [flipped, setFlipped] = useState(() => Math.random() < 0.5);
  useEffect(() => {
    if (loading1 && loading2) setFlipped(Math.random() < 0.5);
  }, [loading1, loading2]);

  const slot1Model = flipped ? option2Model : option1Model;
  const slot2Model = flipped ? option1Model : option2Model;

  // Tell the parent which model ended up in which display slot once both results are loaded
  useEffect(() => {
    if (!loading1 && !loading2 && option1Model && option2Model) {
      onArrange?.({ slot1Model: flipped ? option2Model : option1Model, slot2Model: flipped ? option1Model : option2Model });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading1, loading2, flipped, option1Model, option2Model]);

  const raw = [
    { body: option1, loading: loading1, dotBg: "linear-gradient(135deg, #10a37f, #0d8a6d)" },
    { body: option2, loading: loading2, dotBg: "linear-gradient(135deg, #7856ff, #4f46e5)" },
  ];
  const ordered = flipped ? [raw[1], raw[0]] : raw;
  const options = ordered.map((opt, i) => ({ ...opt, label: `Option ${i + 1}`, displaySlot: i + 1 }));

  return (
    <Box
      sx={{
        borderTop: "1px solid #f1f5f9",
        background: "linear-gradient(180deg, #fbfaff 0%, #ffffff 100%)",
        padding: 14,
      }}
    >
      {/* Header */}
      <Flex align="center" justify="space-between" mb={10}>
        <Flex align="center" gap={8}>
          <Box
            sx={{
              width: 18, height: 18, borderRadius: 6,
              background: "linear-gradient(135deg, #7856ff, #ffaf4a)",
              display: "grid", placeItems: "center",
            }}
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="white">
              <path d="M12 2l2 7 7 2-7 2-2 7-2-7-7-2 7-2 2-7z" />
            </svg>
          </Box>
          <Text fw={600} fz={13} c="#334155">
            {actionLabel} — choose your preferred version
          </Text>
        </Flex>
        <Box
          sx={{
            fontSize: 11, fontWeight: 600, padding: "4px 8px", borderRadius: 999,
            background: "rgba(120,86,255,.08)", color: "#7856ff",
            textTransform: "uppercase", letterSpacing: "0.02em",
          }}
        >
          Compare
        </Box>
      </Flex>

      {/* Options grid */}
      <Flex gap={12}>
        {options.map((opt, i) => (
          <Box
            key={i}
            sx={{
              flex: 1, border: "1.5px solid #e2e8f0", borderRadius: 12,
              padding: 14, background: "#fff", display: "flex",
              flexDirection: "column", minHeight: 140,
            }}
          >
            <Flex align="center" justify="space-between" mb={8}>
              <Flex align="center" gap={6}>
                <Box
                  sx={{
                    width: 18, height: 18, borderRadius: 6, background: opt.dotBg,
                    display: "grid", placeItems: "center", color: "white",
                    fontSize: 10, fontWeight: 700,
                  }}
                >
                  {i + 1}
                </Box>
                <Text fw={700} fz={12}>{opt.label}</Text>
              </Flex>
              <Text fz={11} c="#94a3b8">
                {opt.body ? `${wordCount(opt.body)} words` : ""}
              </Text>
            </Flex>

            {opt.loading ? (
              <Box sx={{ flex: 1 }}>
                <Skeleton height={12} mb={6} radius="md" />
                <Skeleton height={12} mb={6} radius="md" />
                <Skeleton height={12} width="70%" radius="md" />
              </Box>
            ) : (
              <Text fz={13.5} lh={1.55} c="#334155" sx={{ flex: 1, textAlign: "justify" }}>
                {opt.body || ""}
              </Text>
            )}

            <Flex mt={12} gap={8} align="center">
              <Button
                size="xs"
                color="dark"
                sx={{ flex: 1, height: 34, borderRadius: 8, fontWeight: 600, fontSize: 12.5 }}
                onClick={() => onUse(opt.body, { displaySlot: opt.displaySlot, slot1Model, slot2Model })}
                disabled={!opt.body || opt.loading}
              >
                Use this
              </Button>
              <CopyButton value={opt.body || ""}>
                {({ copied, copy }) => (
                  <Tooltip label={copied ? "Copied!" : "Copy"} withArrow>
                    <Button
                      variant="default"
                      sx={{ width: 34, height: 34, borderRadius: 8, padding: 0, display: "grid", placeItems: "center" }}
                      onClick={copy}
                      disabled={!opt.body || opt.loading}
                    >
                      {copied ? (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                      ) : (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h10" /></svg>
                      )}
                    </Button>
                  </Tooltip>
                )}
              </CopyButton>
            </Flex>
          </Box>
        ))}
      </Flex>

      {/* Footer */}
      <Flex align="center" justify="space-between" mt={12}>
        <Button
          variant="subtle"
          color="gray"
          size="xs"
          onClick={onRegenerate}
          leftIcon={
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 12a9 9 0 1 1-3-6.7L21 8" /><polyline points="21 3 21 8 16 8" />
            </svg>
          }
        >
          Regenerate
        </Button>
        <Button variant="subtle" color="gray" size="xs" onClick={onCancel}>
          Cancel
        </Button>
      </Flex>
    </Box>
  );
};

export default ComparePanel;
